import logger from '../../../utils/logger';
/**
 * Unified service for canceling offers
 * Handles both single and bulk offer cancellations
//  * THE VILLAGE LOGIC LIVES IN THE BACKEND, Frontned uses all unified base fields
 */

import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { updateNegotiationObject } from '../SummaryCaseTable/utils/negotiationObjectUpdater.js';
import { updateCaseStatusGeneralized } from '../SummaryCaseTable/services/caseStatusUpdateService.js';
import { updateCourtfilesGeneralized } from '../SummaryCaseTable/services/courtfilesUpdateService.js';
import { createCaseStatusContext, createCourtFilesContext, createOfferContext } from '../../../utils/offerContextUtils';
import { deleteNegotiationEntry } from '../SummaryCaseTable/services/deleteNegotiationService.js';
import { updateOffersBatch } from '../SummaryCaseTable/services/realtimeOfferService.js';
import { usePersistedDataStore } from '../../store';
import { toast } from '../../../common/toast';
import { getMaxNegotiationRoundWithOffers, isOfferSent, isAssignmentComplete } from '../SummaryCaseTable/utils/helpers.js';
import { isTownViewDevNoPersist } from '../../../utils/townViewDevMode.js';
import { confirmVacateStip } from './VacateStipDialog.js';
import { refreshAlgorithmOffers } from '../Settlement/settlementService.js';


// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extract a flat, unified case info object from the negotiation obj for a given parcel.
 * Single source of truth — everything downstream uses this shape.
 */
const extractCaseInfo = (negotiationObj, row) => {
  const caseData = negotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
  if (!caseData) return null;

  const sortedRounds = [...(caseData.Negotiations ?? [])].sort((a, b) => (b.round ?? 0) - (a.round ?? 0));
  const mostRecentRound = sortedRounds[0] ?? null;

  return {
    parcel_id:               caseData.parcel_id,
    Address:                 caseData.Address,
    BARFiled:                caseData.BARFiled,
    BARSettleDate:           caseData.BARSettleDate,
    BARDeterminationAction:  caseData.BARDeterminationAction,
    BARDeterminationValue:   caseData.BARDeterminationValue,

    SCARFiled:               caseData.SCARFiled,
    SCARSettleDate:          caseData.SCARSettleDate,
    SCARDeterminationAction: caseData.SCARDeterminationAction,
    SCARDeterminationValue:  caseData.SCARDeterminationValue,

    isCaseReviewed:          isAssignmentComplete(caseData, 'CaseReview'),
    hasLiveOffer:            isOfferSent(caseData.Negotiations, caseData.parcel_id),
    mostRecentRoundOffers:   mostRecentRound?.offers ?? [],
  };
};

/**
 * Classify each case as 'settlement', 'offer-only', or null (skip).
 * Returns { settlementCases, offerOnlyCases } — each is an array of caseInfo objects.
 */
const classifyCases = (caseInfos) => {
  const settlementCases = [];
  const offerOnlyCases  = [];

  for (const info of caseInfos) {
    if (info.SCARSettleDate || info.BARSettleDate) {
      settlementCases.push(info);
    } else if (info.isCaseReviewed || info.hasLiveOffer) {
      offerOnlyCases.push(info);
    }
    // else: no action needed — skip silently
  }

  return { settlementCases, offerOnlyCases };
};

/**
 * Build the per-parcel courtfiles field map for settlement cancellations.
 * Clears all settlement fields uniformly based on which settle dates exist, plus withdrawal reason.
 */
const buildCourtfilesUpdates = (settlementCases, isVillageRun) => {
  const updates = {};

  logger.log('the settlement cases:', settlementCases);
  for (const { parcel_id, SCARSettleDate, BARSettleDate } of settlementCases) {
    const fields = {};

    if (SCARSettleDate) {
      fields.SCARSettleDate          = null;
      fields.SCARDeterminationAction = null;
      fields.SCARDeterminationValue  = null;
    }

    if (BARSettleDate) {
      fields.BARSettleDate          = null;
      fields.BARDeterminationAction = null;
      fields.BARDeterminationValue  = null;
    }

    fields.Negotiator = null;
    // Clear withdrawal reason at the Courtfiles level as well
    fields[isVillageRun ? 'VillageWithdrawalReason' : 'WithdrawalReason'] = null;

    if (Object.keys(fields).length > 0) updates[parcel_id] = fields;
  }

  return updates;
};

/**
 * Collect offer IDs to delete from the most-recent negotiation round of each settlement case.
 * @param {boolean} [townCancellation] — when true, only Town offers (town portal cancel settlement).
 */
const collectSettlementOfferIds = (settlementCases, townCancellation = false) =>
  settlementCases.flatMap(({ mostRecentRoundOffers }) =>
    mostRecentRoundOffers
      .filter((o) =>
        townCancellation ? o.OfferedBy === 'Town' : o.OfferedBy === 'Aventine' || o.OfferedBy === 'Town',
      )
      .map((o) => o.OfferID ?? o.offer_id)
      .filter(Boolean)
  );

// ─── Backend Steps ────────────────────────────────────────────────────────────

const cancelSettlementOffers = async (settlementCases, taxYear, townCancellation = false) => {
  const offerIds = collectSettlementOfferIds(settlementCases, townCancellation);
  if (offerIds.length > 0) {
    await deleteNegotiationEntry({ offerIds, taxYear });
  }
};

const buildSettlementStipTrackingEntries = (settlementCases, isVillageRun) => {
  const caseType = isVillageRun ? 'Village' : 'Town';
  return settlementCases.flatMap(({ parcel_id, SCARSettleDate, BARSettleDate }) => [
    ...(SCARSettleDate ? [{ parcelId: parcel_id, stage: 'SCAR', caseType }] : []),
    ...(BARSettleDate ? [{ parcelId: parcel_id, stage: 'BAR', caseType }] : []),
  ]);
};

const PROTECTED_STIP_CANCEL_MESSAGE = 'Cannot cancel settlement. The stipulation is So Ordered and cannot be vacated.';

const toastProtectedStipCancel = (error) => {
  const data = error?.response?.data;
  if (!data?.protected_status) return;
  toast.error(
    typeof data.error === 'string' && data.error.startsWith('Cannot cancel settlement')
      ? data.error
      : PROTECTED_STIP_CANCEL_MESSAGE,
    { autoClose: false },
  );
};

const deleteSettlementStipTrackingRows = async (settlementCases, taxYear, isVillageRun, vacateProtected = false) => {
  if (settlementCases.length === 0) return null;
  const entries = buildSettlementStipTrackingEntries(settlementCases, isVillageRun);
  if (!entries.length) return null;
  try {
    const response = await axiosInstance.post('/delete_stiptracking_entries', { entries, taxYear, vacateProtected });
    if (response.data?.confirm_vacate) return response.data.protected_rows || [];
    if (!response.data.success) throw new Error(response.data.error ?? 'Failed to delete StipTracking entries');
    return null;
  } catch (error) {
    toastProtectedStipCancel(error);
    throw error;
  }
};

/**
 * Town settlement cancel: persist Aventine row (clear Response, LiveOffer=1) via the same batch route as settlements / finalize.
 * Uses {@link updateOffersBatch} → POST /create_negotiation_offers_batch. Frontend state is still applied in applyFrontendUpdates.
 */
const persistTownCancelAventineOfferRestores = async (settlementCases, user) => {
  if (!settlementCases.length || !user) return;
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const context = {
    ...createOfferContext(user, negotiationObj, 'Manual'),
    townView: true,
  };

  for (const { parcel_id } of settlementCases) {
    const c = negotiationObj.cases.find((x) => x.parcel_id === parcel_id);
    if (!c?.Negotiations?.length) continue;
    const maxR = getMaxNegotiationRoundWithOffers(c.Negotiations);
    if (maxR <= 0) continue;
    const round = c.Negotiations.find((n) => n.round === maxR);
    const av = round?.offers?.find((o) => o.OfferedBy === 'Aventine');
    if (!av || av.Offer == null) continue;

    const batchOffer = {
      parcelId: parcel_id,
      offeredBy: 'Aventine',
      offerValue: Number(av.Offer),
      response: null,
      comments: av.Comments ?? undefined,
      offerDate: av.OfferDate,
    };

    logger.log('[cancelOffers] persistTownCancelAventineOfferRestores batch offer', { parcel_id, round: maxR, batchOffer });

    const result = await updateOffersBatch([batchOffer], context, {
      round: maxR,
      liveOffer: 1,
      updateFrontend: false,
    });
    if (!result.success) {
      throw new Error(result.error || `Failed to restore Aventine offer for parcel ${parcel_id}`);
    }
  }
};

const cancelFinalizedOffers = async (offerOnlyCases, taxYear) => {
  if (offerOnlyCases.length === 0) return;
  const parcelIds = offerOnlyCases.map(c => c.parcel_id);
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  try {
    const response = await axiosInstance.post('/cancel_offers', {
      parcelIds,
      taxYear,
      villageRun: negotiationObj?.villageRun || 0,
    });
    if (!response.data.success) throw new Error(response.data.error ?? 'Failed to cancel offers');
  } catch (error) {
    toastProtectedStipCancel(error);
    throw error;
  }
};

const clearNegotiationTaskDates = async (allCancelledCases, negotiationObj) => {
  if (allCancelledCases.length === 0) return;
  const context = createCaseStatusContext({ userId: negotiationObj.UserId }, 'Negotiation', negotiationObj);
  const updateCaseStatusParams = {
    ...context,
    parcelIds: allCancelledCases.map(c => c.parcel_id),
    fieldsToUpdate: { DateCompleted: null },
    updateFrontend: true,
    forceUpdateCompleted: true,
  };
  logger.log('[cancelOffers] updateCaseStatusGeneralized (clear negotiation DateCompleted)', updateCaseStatusParams);
  await updateCaseStatusGeneralized(updateCaseStatusParams);
};

// Sent offers only lose LiveOffer. Reviewed offers that were never sent still need CaseReview cleared.
const clearUnsentCaseReview = async (offerOnlyCases, negotiationObj) => {
  const parcelIds = offerOnlyCases
    .filter((c) => c.isCaseReviewed && !c.hasLiveOffer)
    .map((c) => c.parcel_id);
  if (parcelIds.length === 0) return;
  const context = createCaseStatusContext({ userId: negotiationObj.UserId }, 'CaseReview', negotiationObj);
  const updateCaseStatusParams = {
    ...context,
    parcelIds,
    fieldsToUpdate: { DateCompleted: null },
    updateFrontend: true,
    forceUpdateCompleted: true,
  };
  logger.log('[cancelOffers] updateCaseStatusGeneralized (clear unsent CaseReview)', updateCaseStatusParams);
  await updateCaseStatusGeneralized(updateCaseStatusParams);
};

const persistCourtfilesUpdates = async (settlementCases, courtfilesUpdates, negotiationObj) => {
  if (Object.keys(courtfilesUpdates).length === 0) return;

  const stipulation = {
    village: negotiationObj.villageRun === 1,
    year:    negotiationObj.TaxYear,
  };

  const context = createCourtFilesContext({ userId: negotiationObj.UserId }, negotiationObj);
  await updateCourtfilesGeneralized({
    ...context,
    parcelIds:      Object.keys(courtfilesUpdates),
    fieldsToUpdate: courtfilesUpdates,
    stipulation,
    updateFrontend: false,
  });
};

const sendCancellationEmail = async (settlementCases, negotiationObj, user, totalRowCount) => {
  if (settlementCases.length === 0) return;
  try {
    const timestamp = new Date().toISOString();
    const message   = [
      'One or more settlements were cancelled from the Case Review interface.',
      `Timestamp: ${timestamp}`,
      `User: ${user?.userName ?? 'Unknown'} (email: ${user?.email ?? 'N/A'}, id: ${user?.userId ?? user?.id ?? 'N/A'})`,
      `Context: muniCode=${negotiationObj.MuniCode ?? 'N/A'}, muniString=${negotiationObj.muniString ?? 'N/A'}, taxYear=${negotiationObj.TaxYear ?? 'N/A'}.`,
      `Total rows selected: ${totalRowCount}. Settlement cases cancelled: ${settlementCases.length}.`,
      'Cancelled settlement cases:',
      JSON.stringify(
        settlementCases.map(
          ({
            parcel_id,
            Address,
            SCARFiled,
            BARFiled,
            SCARSettleDate,
            BARSettleDate,
            SCARDeterminationAction,
            SCARDeterminationValue,
            BARDeterminationAction,
            BARDeterminationValue,
          }) => ({
            parcel_id,
            Address,
            SCARFiled,
            BARFiled,
            SCARSettleDate,
            BARSettleDate,
            SCARDeterminationAction,
            SCARDeterminationValue,
            BARDeterminationAction,
            BARDeterminationValue,
          })
        ),
        null,
        2
      ),
    ].join('\n\n');

    await axiosInstance.post('/send_internal_email', {
      toAddress: ['jrburns@aventine.ai'],
      subject: 'Settlement cancellation notification',
      message,
    });
  } catch (e) {
    logger.error('Failed to send settlement cancellation email:', e);
  }
};

// ─── Frontend Update ──────────────────────────────────────────────────────────

/**
 * Build per-case field updates for the negotiation object.
 * Clears all settlement-related fields uniformly based on which settle dates exist.
 * Shows a warning toast if both BAR and SCAR settlements are being cleared simultaneously.
 */
const buildCaseFieldUpdates = (caseInfo, { preserveOfferValues, isVillageRun }) => {
  const fields = {};

  logger.log('[cancelOffers] buildCaseFieldUpdates - input caseInfo', {
    parcel_id: caseInfo.parcel_id,
    SCARSettleDate: caseInfo.SCARSettleDate,
    BARSettleDate: caseInfo.BARSettleDate,
    isCaseReviewed: caseInfo.isCaseReviewed,
    hasLiveOffer: caseInfo.hasLiveOffer,
  });

  const stipInfo = caseInfo.StipInfo && typeof caseInfo.StipInfo === 'object' ? { ...caseInfo.StipInfo } : {};

  if (caseInfo.SCARSettleDate) {
    fields.SCARSettleDate          = null;
    fields.SCARDeterminationAction = null;
    fields.SCARDeterminationValue  = null;
    stipInfo.scar = null;
  }

  if (caseInfo.BARSettleDate) {
    fields.BARSettleDate          = null;
    fields.BARDeterminationAction = null;
    fields.BARDeterminationValue  = null;
    stipInfo.bar = null;
  }

  if (caseInfo.SCARSettleDate || caseInfo.BARSettleDate) {
    fields.StipInfo = stipInfo;
  }

  if (caseInfo.SCARSettleDate && caseInfo.BARSettleDate) {
    toast.warning('BOTH BAR and SCAR settlements were cleared. This should not be possible. This error has been flagged to the team.');
    sendCancellationEmail(
      [caseInfo],
      usePersistedDataStore.getState().negotiationObj,
      null,
      1,
      { subject: '[ERROR] Both BAR and SCAR settlements cleared on single parcel' }
    );
  }

  fields[isVillageRun ? 'VillageWithdrawalReason' : 'WithdrawalReason'] = null;

  if (!preserveOfferValues) fields.OfferValue = null;

  logger.log('[cancelOffers] buildCaseFieldUpdates - output fields', {
    parcel_id: caseInfo.parcel_id,
    isVillageRun,
    preserveOfferValues,
    fields,
  });

  return fields;
};

const applyFrontendUpdates = (
  { settlementCases, offerOnlyCases },
  { preserveOfferValues, isVillageRun, townCancellation },
  { setNegotiationObj, setNegotiationObjVersion }
) => {
  let updated = usePersistedDataStore.getState().negotiationObj;

  if (settlementCases.length > 0) {
    // Enrich each case with its own computed field updates, then batch all at once.
    // updateNegotiationObject will spread _caseFieldUpdates onto each matched case.
    logger.log('[cancelOffers] applyFrontendUpdates - raw settlementCases', settlementCases);

    const enrichedSettlementCases = settlementCases.map(c => {
      const caseFieldUpdates = buildCaseFieldUpdates(c, { preserveOfferValues, isVillageRun });
      logger.log('[cancelOffers] applyFrontendUpdates - per-case updates', {
        parcel_id: c.parcel_id,
        caseFieldUpdates,
      });
      return { ...c, caseFieldUpdates };
    });

    logger.log('[cancelOffers] applyFrontendUpdates - enrichedSettlementCases payload', enrichedSettlementCases);

    updated = updateNegotiationObject(updated, enrichedSettlementCases, {
      negotiationsUpdates: townCancellation
        ? { townCancelRestoreAventineLiveOffer: true }
        : { setLiveOfferToZero: true },
      // caseFieldUpdates read per-case from caseItem.caseFieldUpdates inside updateNegotiationObject
    });
  }

  if (offerOnlyCases.length > 0 && !townCancellation) {
    logger.log('[cancelOffers] applyFrontendUpdates - offerOnlyCases', offerOnlyCases);

    updated = updateNegotiationObject(updated, offerOnlyCases, {
      negotiationsUpdates: { setLiveOfferToZero: true },
      ...(!preserveOfferValues ? { caseFieldUpdates: { OfferValue: null } } : {}),
    });
  }

  setNegotiationObj(updated);
  setNegotiationObjVersion(v => v + 1);
};

const refreshMunicipalResultsAfterCancel = (townCancellation) => {
  if (townCancellation && isTownViewDevNoPersist(true)) return;
  usePersistedDataStore.getState().refreshMunicipalResults?.();
};

// ─── Main Export ──────────────────────────────────────────────────────────────

/**
 * @param {object} options
 * @param {boolean} [options.townCancellation] — Town portal: skip `/cancel_offers`; delete only Town negotiation rows for settlements; then persist Aventine clear-Response + LiveOffer=1 via {@link updateOffersBatch}.
 */
export const cancelOffers = async ({
  selectedRows,
  getNegotiationObj,
  setNegotiationObj,
  setNegotiationObjVersion,
  preserveOfferValues = true,
  user,
  subjectParcelId,
  townCancellation = false,
}) => {
  if (user?.userAccess?.level === 'Training') {
    throw new Error('Training users cannot cancel offers. This action is restricted.');
  }
  if (usePersistedDataStore.getState().stipsDownloadInFlight) {
    throw new Error('Cannot cancel settlement while stipulations are downloading.');
  }

  // Resolve which rows to process
  const rowsToProcess = subjectParcelId
    ? [getNegotiationObj.cases.find(c => c.parcel_id === subjectParcelId)].filter(Boolean)
    : selectedRows ?? [];

  if (rowsToProcess.length === 0) {
    throw new Error('No rows selected or subject parcel ID provided.');
  }

  // Build unified case info objects — single lookup per parcel
  const caseInfos = rowsToProcess.map(row => extractCaseInfo(getNegotiationObj, row)).filter(Boolean);

  // Classify
  const { settlementCases, offerOnlyCases } = classifyCases(caseInfos);
  logger.log('[cancelOffers] cancelOffers - classification result', {
    caseInfos,
    settlementCases,
    offerOnlyCases,
  });

  const allCancelledCases = [...settlementCases, ...offerOnlyCases];

  const { TaxYear, villageRun } = getNegotiationObj;
  const courtfilesUpdates = buildCourtfilesUpdates(settlementCases, villageRun === 1);
  logger.log('[cancelOffers] cancelOffers - courtfilesUpdates payload', courtfilesUpdates);

  if (townCancellation && isTownViewDevNoPersist(true)) {
    logger.log('[TownView dev] Skipping cancel settlement DB persist — FE store only');
    applyFrontendUpdates(
      { settlementCases, offerOnlyCases },
      { preserveOfferValues, isVillageRun: villageRun === 1, townCancellation },
      { setNegotiationObj, setNegotiationObjVersion },
    );
    refreshMunicipalResultsAfterCancel(townCancellation);
    return {
      success: true,
      cancelledCount: allCancelledCases.length,
      settlementCancelledCount: settlementCases.length,
      parcelIds: rowsToProcess.map((r) => r.parcel_id),
    };
  }

  // ── Backend ──────────────────────────────────────────────────────────────
  const vacateRows = await deleteSettlementStipTrackingRows(settlementCases, TaxYear, villageRun === 1);
  if (vacateRows?.length) {
    const addressByParcel = Object.fromEntries(settlementCases.map((c) => [c.parcel_id, c.Address]));
    const confirmed = await confirmVacateStip(vacateRows.map((row) => ({
      ...row,
      address: addressByParcel[row.parcelId],
    })));
    if (!confirmed) {
      const declined = new Error('Settlement cancel was not confirmed.');
      declined.vacateDeclined = true;
      throw declined;
    }
    await deleteSettlementStipTrackingRows(settlementCases, TaxYear, villageRun === 1, true);
  }

  if (townCancellation) {
    await Promise.all([
      cancelSettlementOffers(settlementCases, TaxYear, townCancellation),
      persistTownCancelAventineOfferRestores(settlementCases, user),
    ]);
  } else {
    await Promise.all([
      cancelSettlementOffers(settlementCases, TaxYear, townCancellation),
      cancelFinalizedOffers(offerOnlyCases, TaxYear),
    ]);
  }

  await Promise.all([
    (async () => {
      await clearNegotiationTaskDates(allCancelledCases, getNegotiationObj);
      if (!townCancellation) await clearUnsentCaseReview(offerOnlyCases, getNegotiationObj);
    })(),
    persistCourtfilesUpdates(settlementCases, courtfilesUpdates, getNegotiationObj),
  ]);

  // Fire-and-forget email notification (non-blocking)
  sendCancellationEmail(settlementCases, getNegotiationObj, user, rowsToProcess.length);

  // ── Frontend ─────────────────────────────────────────────────────────────
  applyFrontendUpdates(
    { settlementCases, offerOnlyCases },
    { preserveOfferValues, isVillageRun: villageRun === 1, townCancellation },
    { setNegotiationObj, setNegotiationObjVersion }
  );

  refreshMunicipalResultsAfterCancel(townCancellation);
  if (!townCancellation && settlementCases.length > 0) {
    refreshAlgorithmOffers().catch((error) => logger.error('Refresh algo offers after cancel settlement failed:', error));
  }

  return {
    success: true,
    cancelledCount:           allCancelledCases.length,
    settlementCancelledCount: settlementCases.length,
    parcelIds:                rowsToProcess.map(r => r.parcel_id),
  };
};